import jsPDF from 'jspdf';

// Color palette matching the app theme
const COLORS = {
  primary: [26, 54, 45],      // #1A362D - Deep moss
  secondary: [45, 74, 62],    // #2D4A3E - Hover moss
  terracotta: [200, 90, 50],  // #C85A32
  sage: [139, 157, 143],      // #8B9D8F
  sky: [120, 152, 165],       // #7898A5
  text: [17, 24, 20],         // #111814
  muted: [107, 114, 128],     // #6B7280
  light: [249, 248, 246],     // #F9F8F6
  white: [255, 255, 255],
  border: [229, 225, 216],    // #E5E1D8
  error: [163, 59, 59],       // #A33B3B
  success: [45, 74, 62],      // #2D4A3E
};

const INDUSTRY_NAMES = {
  'manufacturing': 'Manufacturing',
  'office': 'Office / Commercial',
  'retail': 'Retail',
  'logistics': 'Logistics & Warehousing',
  'hospitality': 'Hospitality & Hotels'
};

// Fixed formatting function with proper spacing
const formatNumber = (num, addSpace = false) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + (addSpace ? ' M' : 'M');
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + (addSpace ? ' K' : 'K');
  }
  return num.toLocaleString();
};

const formatDate = () => {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const generateESGReport = async (dashboardData, benchmarks, risks, targets, companySettings = {}) => {
  const { companyName = 'Demo Company Ltd.', industry = 'office', logoUrl = '' } = companySettings;
  const industryName = INDUSTRY_NAMES[industry] || 'Office / Commercial';
  
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - (margin * 2);
  let y = margin;

  // Helper functions
  const setFont = (style = 'normal', size = 10) => {
    doc.setFontSize(size);
    doc.setFont('helvetica', style);
  };

  const setColor = (color) => {
    doc.setTextColor(...color);
  };

  const drawLine = (yPos, color = COLORS.border, width = 0.3) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(width);
    doc.line(margin, yPos, pageWidth - margin, yPos);
  };

  const addSectionTitle = (title, yPos, hasUnderline = true) => {
    setFont('bold', 11);
    setColor(COLORS.primary);
    doc.text(title.toUpperCase(), margin, yPos);
    if (hasUnderline) {
      drawLine(yPos + 2, COLORS.primary, 0.5);
    }
    return yPos + 12;
  };

  // Get data
  const totalEmissions = dashboardData?.total_emissions || 45320.5;
  const scope1 = dashboardData?.scope_emissions?.['Scope 1'] || 12500;
  const scope2 = dashboardData?.scope_emissions?.['Scope 2'] || 18200;
  const scope3 = dashboardData?.scope_emissions?.['Scope 3'] || 14620;
  const energy = dashboardData?.energy_consumption || 85000;
  const water = dashboardData?.water_usage || 125000;
  const waste = dashboardData?.waste_generation || 3500;
  
  const highRisks = (risks || []).filter(r => r.priority === 'Critical' || r.priority === 'High').length;
  const aboveBenchmark = (benchmarks || []).filter(b => b.status === 'above').length;

  // ==================== PAGE 1 ====================
  
  // Header bar with gradient effect
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 52, 'F');
  
  // Accent bar
  doc.setFillColor(...COLORS.terracotta);
  doc.rect(0, 52, pageWidth, 3, 'F');

  // ESG VANITIX Branding
  setFont('bold', 11);
  setColor(COLORS.white);
  doc.text('ESG', margin, 18);
  doc.setTextColor(...COLORS.terracotta);
  doc.text('VANITIX', margin + 12, 18);
  
  // Report Title
  setFont('bold', 24);
  setColor(COLORS.white);
  doc.text('ESG Intelligence Report', margin, 34);

  // Subtitle
  setFont('normal', 9);
  doc.setTextColor(255, 255, 255);
  doc.text('Values  |  Analytics  |  Net Zero  |  Intelligence  |  Transformation  |  Execution', margin, 45);

  y = 68;

  // Company Info Card
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(margin, y, contentWidth, 28, 2, 2, 'F');
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(margin, y, contentWidth, 28, 2, 2, 'S');
  
  // Company name
  setFont('bold', 14);
  setColor(COLORS.text);
  doc.text(companyName, margin + 10, y + 11);
  
  // Industry badge
  setFont('normal', 9);
  setColor(COLORS.muted);
  doc.text(`Industry: ${industryName}`, margin + 10, y + 19);
  
  // Date info (right aligned)
  setFont('normal', 9);
  const dateText = `Generated: ${formatDate()}`;
  const dateWidth = doc.getTextWidth(dateText);
  doc.text(dateText, pageWidth - margin - dateWidth - 10, y + 11);
  doc.text('Reporting Period: FY 2024', pageWidth - margin - dateWidth - 10, y + 19);

  y = 108;

  // ==================== EXECUTIVE SUMMARY ====================
  y = addSectionTitle('Executive Summary', y);

  setFont('normal', 9);
  setColor(COLORS.text);
  
  const summaryText = `This report presents the environmental baseline assessment for ${companyName}. The organization's total carbon footprint stands at ${formatNumber(totalEmissions, true)} kg CO₂e, distributed across Scope 1 (${Math.round(scope1/totalEmissions*100)}%), Scope 2 (${Math.round(scope2/totalEmissions*100)}%), and Scope 3 (${Math.round(scope3/totalEmissions*100)}%) emissions.

Analysis reveals ${aboveBenchmark} metrics exceeding ${industryName.toLowerCase()} industry benchmarks, with ${highRisks} high-priority ESG risks identified. The assessment follows GHG Protocol Corporate Standard methodology and aligns with GRI, BRSR, TCFD, and IFRS S2 frameworks.`;

  const summaryLines = doc.splitTextToSize(summaryText, contentWidth);
  doc.text(summaryLines, margin, y);
  y += summaryLines.length * 4.5 + 8;

  // Key Finding Box
  doc.setFillColor(...COLORS.terracotta);
  doc.roundedRect(margin, y, contentWidth, 16, 2, 2, 'F');
  
  setFont('bold', 8);
  setColor(COLORS.white);
  doc.text('KEY FINDING', margin + 6, y + 6);
  
  setFont('normal', 8);
  doc.text('Electricity consumption exceeds industry benchmark. Transitioning to renewable energy could reduce', margin + 6, y + 11);
  doc.text('Scope 2 emissions by up to 50% with estimated annual savings.', margin + 6, y + 15);

  y += 26;

  // ==================== KEY METRICS ====================
  y = addSectionTitle('Key Environmental Metrics', y);

  // Metrics cards - improved layout
  const metricBoxWidth = (contentWidth - 12) / 4;
  const metrics = [
    { label: 'TOTAL EMISSIONS', value: totalEmissions, unit: 'kg CO₂e', color: COLORS.terracotta },
    { label: 'ENERGY', value: energy, unit: 'kWh', color: COLORS.sky },
    { label: 'WATER', value: water, unit: 'Liters', color: COLORS.sky },
    { label: 'WASTE', value: waste, unit: 'kg', color: COLORS.sage }
  ];

  metrics.forEach((metric, i) => {
    const x = margin + (i * (metricBoxWidth + 4));
    
    // Card background
    doc.setFillColor(...COLORS.white);
    doc.setDrawColor(...COLORS.border);
    doc.roundedRect(x, y, metricBoxWidth, 32, 2, 2, 'FD');
    
    // Top color bar
    doc.setFillColor(...metric.color);
    doc.roundedRect(x, y, metricBoxWidth, 5, 2, 2, 'F');
    doc.rect(x, y + 3, metricBoxWidth, 2, 'F');
    
    // Label
    setFont('bold', 7);
    setColor(COLORS.muted);
    doc.text(metric.label, x + 6, y + 13);
    
    // Value (with proper spacing)
    setFont('bold', 16);
    setColor(COLORS.text);
    const formattedValue = formatNumber(metric.value, false);
    doc.text(formattedValue, x + 6, y + 23);
    
    // Unit (on separate line for clarity)
    setFont('normal', 8);
    setColor(COLORS.muted);
    doc.text(metric.unit, x + 6, y + 29);
  });

  y += 42;

  // Scope breakdown card
  doc.setFillColor(...COLORS.white);
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(margin, y, contentWidth, 20, 2, 2, 'FD');

  const scopes = [
    { name: 'Scope 1 (Direct)', value: scope1, color: COLORS.terracotta, percent: Math.round(scope1/totalEmissions*100) },
    { name: 'Scope 2 (Energy)', value: scope2, color: COLORS.sky, percent: Math.round(scope2/totalEmissions*100) },
    { name: 'Scope 3 (Value Chain)', value: scope3, color: COLORS.sage, percent: Math.round(scope3/totalEmissions*100) }
  ];

  const scopeWidth = contentWidth / 3;
  scopes.forEach((scope, i) => {
    const x = margin + (i * scopeWidth) + 8;
    
    // Color indicator
    doc.setFillColor(...scope.color);
    doc.circle(x, y + 10, 3, 'F');
    
    // Name and value
    setFont('normal', 8);
    setColor(COLORS.muted);
    doc.text(scope.name, x + 6, y + 8);
    
    setFont('bold', 10);
    setColor(COLORS.text);
    doc.text(`${formatNumber(scope.value, true)} kg  (${scope.percent}%)`, x + 6, y + 15);
  });

  y += 30;

  // ==================== BENCHMARK TABLE ====================
  y = addSectionTitle('Industry Benchmark Comparison', y);

  // Table header
  doc.setFillColor(...COLORS.primary);
  doc.rect(margin, y, contentWidth, 9, 'F');
  
  // Column positions (evenly distributed)
  const col1 = margin + 6;
  const col2 = margin + 58;
  const col3 = margin + 95;
  const col4 = margin + 130;
  const col5 = margin + 158;
  
  setFont('bold', 8);
  setColor(COLORS.white);
  doc.text('METRIC', col1, y + 6);
  doc.text('ACTUAL', col2, y + 6);
  doc.text('BENCHMARK', col3, y + 6);
  doc.text('DEVIATION', col4, y + 6);
  doc.text('STATUS', col5, y + 6);

  y += 9;

  // Benchmark rows with proper alignment
  const benchmarkData = benchmarks || [
    { metric: 'Electricity Intensity', actual: 85, benchmark: 70, unit: 'kWh/sqm', deviation_percent: 21.4, status: 'above' },
    { metric: 'Carbon Intensity', actual: 45.3, benchmark: 40, unit: 'tCO₂e/₹Cr', deviation_percent: 13.3, status: 'above' },
    { metric: 'Water Intensity', actual: 125, benchmark: 150, unit: 'kL/emp', deviation_percent: -16.7, status: 'within' },
    { metric: 'Waste Generation', actual: 35, benchmark: 50, unit: 'kg/emp', deviation_percent: -30, status: 'within' }
  ];

  benchmarkData.slice(0, 4).forEach((b, i) => {
    const rowY = y + (i * 9);
    
    // Alternating row background
    if (i % 2 === 0) {
      doc.setFillColor(...COLORS.light);
    } else {
      doc.setFillColor(...COLORS.white);
    }
    doc.rect(margin, rowY, contentWidth, 9, 'F');
    
    // Grid lines
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.1);
    doc.line(margin, rowY + 9, pageWidth - margin, rowY + 9);

    // Metric name (left aligned)
    setFont('normal', 8);
    setColor(COLORS.text);
    doc.text(b.metric, col1, rowY + 6);
    
    // Actual value (left aligned with unit)
    doc.text(`${b.actual} ${b.unit}`, col2, rowY + 6);
    
    // Benchmark value (left aligned)
    doc.text(`${b.benchmark}`, col3, rowY + 6);
    
    // Deviation with color (left aligned)
    const deviationText = `${b.deviation_percent > 0 ? '+' : ''}${b.deviation_percent.toFixed(1)}%`;
    setColor(b.status === 'within' ? COLORS.success : COLORS.terracotta);
    setFont('bold', 8);
    doc.text(deviationText, col4, rowY + 6);
    
    // Status badge
    const statusColor = b.status === 'within' ? COLORS.success : COLORS.terracotta;
    doc.setFillColor(...statusColor);
    doc.roundedRect(col5 - 2, rowY + 1.5, 20, 6, 1, 1, 'F');
    setFont('bold', 6);
    setColor(COLORS.white);
    const statusText = b.status === 'within' ? 'WITHIN' : 'ABOVE';
    doc.text(statusText, col5, rowY + 5.5);
  });

  y += 40;

  // Source note
  setFont('normal', 7);
  setColor(COLORS.muted);
  doc.text(`Source: BEE, IEA, CPCB benchmarks for ${industryName} sector`, margin, y);

  // ==================== PAGE 1 FOOTER ====================
  const footer1Y = pageHeight - 12;
  drawLine(footer1Y - 4, COLORS.border);
  
  setFont('normal', 7);
  setColor(COLORS.muted);
  doc.text('ESG VANITIX  |  Values  |  Analytics  |  Net Zero  |  Intelligence  |  Transformation  |  Execution', margin, footer1Y);
  
  setFont('normal', 7);
  const page1Text = 'Page 1 of 2';
  const page1Width = doc.getTextWidth(page1Text);
  doc.text(page1Text, pageWidth - margin - page1Width, footer1Y);

  // ==================== PAGE 2 ====================
  doc.addPage();
  y = margin;

  // Page 2 header
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 20, 'F');
  
  setFont('bold', 10);
  setColor(COLORS.white);
  doc.text('ESG', margin, 13);
  doc.setTextColor(...COLORS.terracotta);
  doc.text('VANITIX', margin + 12, 13);
  
  setFont('normal', 10);
  setColor(COLORS.white);
  doc.text('ESG Intelligence Report', margin + 45, 13);
  
  setFont('normal', 8);
  const companyTextWidth = doc.getTextWidth(companyName);
  doc.text(companyName, pageWidth - margin - companyTextWidth, 13);

  y = 32;

  // ==================== RISKS & CONTROLS ====================
  y = addSectionTitle('Key ESG Risks & Controls', y);

  const riskData = risks || [
    { risk_id: 'R001', risk_description: 'High energy costs and carbon exposure', impact_level: 'High', priority: 'Critical', suggested_control: 'Implement energy efficiency measures: LED, HVAC optimization' },
    { risk_id: 'R002', risk_description: 'Fleet emission and fuel cost volatility', impact_level: 'Medium', priority: 'High', suggested_control: 'Fleet optimization, consider EV/hybrid transition' },
    { risk_id: 'R003', risk_description: 'Regulatory risk from RE mandates', impact_level: 'Medium', priority: 'High', suggested_control: 'Increase renewable energy procurement via PPAs' }
  ];

  riskData.slice(0, 3).forEach((risk, i) => {
    const boxY = y + (i * 24);
    
    // Risk card
    const priorityColor = risk.priority === 'Critical' ? COLORS.error : 
                         risk.priority === 'High' ? COLORS.terracotta : COLORS.sage;
    
    doc.setFillColor(...COLORS.light);
    doc.setDrawColor(...COLORS.border);
    doc.roundedRect(margin, boxY, contentWidth, 22, 2, 2, 'FD');
    
    // Priority indicator bar
    doc.setFillColor(...priorityColor);
    doc.rect(margin, boxY, 4, 22, 'F');
    
    // Risk ID and Priority badge
    setFont('bold', 8);
    setColor(priorityColor);
    doc.text(risk.risk_id, margin + 10, boxY + 7);
    
    // Priority badge
    doc.setFillColor(...priorityColor);
    doc.roundedRect(margin + 25, boxY + 2, 18, 6, 1, 1, 'F');
    setFont('bold', 6);
    setColor(COLORS.white);
    doc.text(risk.priority.toUpperCase(), margin + 27, boxY + 6);
    
    // Risk description
    setFont('normal', 9);
    setColor(COLORS.text);
    doc.text(risk.risk_description, margin + 50, boxY + 7);
    
    // Control label and text
    setFont('bold', 7);
    setColor(COLORS.muted);
    doc.text('CONTROL:', margin + 10, boxY + 16);
    
    setFont('normal', 8);
    setColor(COLORS.text);
    doc.text(risk.suggested_control, margin + 32, boxY + 16);
  });

  y += 82;

  // ==================== RECOMMENDATIONS ====================
  y = addSectionTitle('Strategic Recommendations', y);

  const recommendations = [
    { num: '1', title: 'Energy Efficiency Program', desc: 'Implement LED lighting, HVAC optimization. Target: 15-20% reduction.', timeline: '6-12 months' },
    { num: '2', title: 'Renewable Energy Transition', desc: 'Procure 50% electricity from renewables via PPAs or green tariffs.', timeline: '12-18 months' },
    { num: '3', title: 'Fleet Optimization', desc: 'Route optimization, driver training, 20% EV/hybrid transition.', timeline: '18-24 months' }
  ];

  recommendations.forEach((rec, i) => {
    const boxY = y + (i * 20);
    
    // Card
    doc.setFillColor(...COLORS.white);
    doc.setDrawColor(...COLORS.border);
    doc.roundedRect(margin, boxY, contentWidth, 18, 2, 2, 'FD');
    
    // Number circle
    doc.setFillColor(...COLORS.primary);
    doc.circle(margin + 10, boxY + 9, 5, 'F');
    setFont('bold', 10);
    setColor(COLORS.white);
    doc.text(rec.num, margin + 8, boxY + 11);
    
    // Title
    setFont('bold', 9);
    setColor(COLORS.text);
    doc.text(rec.title, margin + 22, boxY + 7);
    
    // Description
    setFont('normal', 8);
    setColor(COLORS.muted);
    doc.text(rec.desc, margin + 22, boxY + 13);
    
    // Timeline badge (right aligned)
    setFont('normal', 7);
    setColor(COLORS.muted);
    const timelineWidth = doc.getTextWidth(rec.timeline);
    doc.text(rec.timeline, pageWidth - margin - timelineWidth - 6, boxY + 10);
  });

  y += 70;

  // ==================== TARGETS ====================
  y = addSectionTitle('Emission Reduction Targets (SBTi Aligned)', y);

  // Target summary card
  doc.setFillColor(...COLORS.primary);
  doc.roundedRect(margin, y, contentWidth, 32, 2, 2, 'F');

  setFont('bold', 10);
  setColor(COLORS.white);
  doc.text('Science-Based Targets Initiative (SBTi) Aligned', margin + 10, y + 10);

  setFont('normal', 8);
  doc.text('Targets set following 1.5°C pathway methodology', margin + 10, y + 18);

  // Target values
  const baseEmissions = totalEmissions;
  const target2030 = Math.round(baseEmissions * 0.58);
  const target2050 = Math.round(baseEmissions * 0.1);

  // 2030 Target
  setFont('bold', 16);
  doc.text('-42%', margin + 115, y + 14);
  setFont('normal', 8);
  doc.text('by 2030', margin + 137, y + 14);
  
  // 2050 Target
  setFont('bold', 16);
  doc.text('-90%', margin + 155, y + 14);
  setFont('normal', 8);
  doc.text('by 2050', margin + 177, y + 14);

  y += 40;

  // Target pathway visualization
  doc.setFillColor(...COLORS.light);
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(margin, y, contentWidth, 28, 2, 2, 'FD');

  // Timeline line
  const lineY = y + 14;
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(2);
  doc.line(margin + 25, lineY, pageWidth - margin - 25, lineY);

  // Milestone points
  const milestones = [
    { year: '2024', label: 'Baseline', value: `${formatNumber(baseEmissions, true)} kg`, x: margin + 25 },
    { year: '2030', label: 'Near-term', value: `${formatNumber(target2030, true)} kg`, x: margin + 90 },
    { year: '2050', label: 'Net Zero', value: `${formatNumber(target2050, true)} kg`, x: pageWidth - margin - 25 }
  ];

  milestones.forEach(m => {
    // Point
    doc.setFillColor(...COLORS.primary);
    doc.circle(m.x, lineY, 4, 'F');
    
    // Year
    setFont('bold', 8);
    setColor(COLORS.primary);
    doc.text(m.year, m.x - 6, lineY - 8);
    
    // Label and value
    setFont('normal', 7);
    setColor(COLORS.muted);
    doc.text(m.label, m.x - 10, lineY + 8);
    
    setFont('bold', 8);
    setColor(COLORS.text);
    doc.text(m.value, m.x - 12, lineY + 14);
  });

  y += 36;

  // Disclaimer
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(margin, y, contentWidth, 12, 2, 2, 'F');
  
  setFont('normal', 6);
  setColor(COLORS.muted);
  const disclaimer = 'Note: Simplified SBTi methodology demonstration. Official validation requires detailed criteria and third-party verification.';
  doc.text(disclaimer, margin + 6, y + 7);

  // ==================== PAGE 2 FOOTER ====================
  const footer2Y = pageHeight - 12;
  drawLine(footer2Y - 4, COLORS.border);
  
  setFont('normal', 7);
  setColor(COLORS.muted);
  doc.text('ESG VANITIX  |  Values  |  Analytics  |  Net Zero  |  Intelligence  |  Transformation  |  Execution', margin, footer2Y);
  
  setFont('normal', 7);
  const page2Text = 'Page 2 of 2';
  const page2Width = doc.getTextWidth(page2Text);
  doc.text(page2Text, pageWidth - margin - page2Width, footer2Y);

  // Save the PDF
  const sanitizedCompanyName = companyName.replace(/[^a-zA-Z0-9]/g, '_');
  doc.save(`ESG_VANITIX_Report_${sanitizedCompanyName}.pdf`);
};

export default generateESGReport;
